/**
 * Step 1: Trajet - Nouveau design
 * - Adresses départ/arrivée avec détection auto aéroport/gare
 * - Numéro de vol/train si détecté
 * - Sélection du véhicule avec prix en temps réel
 * - Aller-retour avec remise
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useBooking } from '../../context/BookingContext';
import { pricingAPI } from '../../services/bookingApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent } from '../ui/card';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import axios from 'axios';
import { 
  Calendar, ArrowRight, AlertCircle, Plus, Trash2, 
  MapPin, Navigation, Loader2, Car, Users, Briefcase,
  Plane, Train, Check, Tag, Info, Moon
} from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

// Keywords pour détecter les aéroports et gares
const AIRPORT_KEYWORDS = ['aéroport', 'airport', 'cdg', 'orly', 'beauvais', 'terminal', 'roissy'];
const TRAIN_STATION_KEYWORDS = ['gare', 'station', 'sncf', 'tgv', 'eurostar', 'montparnasse', 'saint-lazare', 'lyon', 'nord', 'est', 'austerlitz'];

const Step1Trip = () => {
  const { bookingData, updateBookingData, nextStep, errors, clearErrors } = useBooking();
  const [serviceHours, setServiceHours] = useState({ start_hour: 6, end_hour: 23 });
  const [validationErrors, setValidationErrors] = useState({});
  const [intermediateStops, setIntermediateStops] = useState(bookingData.intermediate_stops || []);
  const [geolocating, setGeolocating] = useState(false);
  
  // Détection aéroport/gare
  const [pickupType, setPickupType] = useState(null); // 'airport' | 'train' | null
  const [dropoffType, setDropoffType] = useState(null);
  
  // Véhicules et prix
  const [vehicles, setVehicles] = useState([]);
  const [vehiclePrices, setVehiclePrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [priceError, setPriceError] = useState(null);

  // Charger les options de service et véhicules
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/bookings/options`);
        setVehicles(response.data.vehicle_categories || []);
        setServiceHours(response.data.service_hours || { start_hour: 6, end_hour: 23 });
      } catch (err) {
        console.error('Error loading options:', err);
      }
    };
    loadData();
    
    // Set default date/time
    if (!bookingData.pickup_datetime) {
      const now = new Date();
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
      now.setSeconds(0);
      now.setHours(now.getHours() + 2); // +2h par défaut
      const formattedDateTime = now.toISOString().slice(0, 16);
      updateBookingData({ pickup_datetime: formattedDateTime });
    }
    
    // Set default vehicle
    if (!bookingData.vehicle_category) {
      updateBookingData({ vehicle_category: 'berline' });
    }
  }, []);

  // Détecter le type de lieu (aéroport/gare)
  const detectLocationType = useCallback((address) => {
    if (!address) return null;
    const addressLower = address.toLowerCase();
    
    if (AIRPORT_KEYWORDS.some(kw => addressLower.includes(kw))) {
      return 'airport';
    }
    if (TRAIN_STATION_KEYWORDS.some(kw => addressLower.includes(kw))) {
      return 'train';
    }
    return null;
  }, []);

  // Mettre à jour la détection quand les adresses changent
  useEffect(() => {
    setPickupType(detectLocationType(bookingData.pickup?.address));
  }, [bookingData.pickup?.address, detectLocationType]);

  useEffect(() => {
    setDropoffType(detectLocationType(bookingData.dropoff?.address));
  }, [bookingData.dropoff?.address, detectLocationType]);

  // Calculer les prix pour tous les véhicules
  // État pour la distance et durée totales
  const [routeInfo, setRouteInfo] = useState({ distance_km: null, duration_min: null });

  const calculateAllPrices = useCallback(async () => {
    if (!bookingData.pickup?.latitude || !bookingData.dropoff?.latitude) {
      setVehiclePrices({});
      setRouteInfo({ distance_km: null, duration_min: null });
      return;
    }

    setLoadingPrices(true);
    setPriceError(null);
    
    try {
      // Préparer les étapes intermédiaires avec coordonnées valides
      const validStops = intermediateStops.filter(stop => 
        stop.latitude && stop.longitude && stop.address
      );

      const pricePromises = vehicles.map(async (vehicle) => {
        try {
          const response = await pricingAPI.calculatePrice({
            pickup_address: bookingData.pickup.address,
            pickup_lat: bookingData.pickup.latitude,
            pickup_lng: bookingData.pickup.longitude,
            dropoff_address: bookingData.dropoff.address,
            dropoff_lat: bookingData.dropoff.latitude,
            dropoff_lng: bookingData.dropoff.longitude,
            pickup_datetime: bookingData.pickup_datetime || new Date().toISOString(),
            vehicle_category: vehicle.category,
            service_type: 'transfer',
            passengers: bookingData.passengers || 1,
            luggage: bookingData.luggage || 0,
            is_round_trip: bookingData.is_round_trip || false,
            intermediate_stops: validStops.map(stop => ({
              address: stop.address,
              latitude: stop.latitude,
              longitude: stop.longitude
            }))
          });
          
          // Extraire distance et durée
          const distanceKm = response.price_details?.inputs?.route?.distance_km || 
                            response.price_details?.base_calculation?.distance_km || 0;
          const durationMin = response.price_details?.inputs?.route?.duration_min ||
                             response.price_details?.base_calculation?.estimated_duration_min || 0;
          
          // Vérifier la distance minimale
          const minDistanceKm = response.price_details?.base_calculation?.min_distance_km || 0;
          const isUnavailable = minDistanceKm > 0 && distanceKm < minDistanceKm;
          
          return {
            category: vehicle.category,
            price: response.price_details?.total_ttc || 0,
            basePrice: response.price_details?.base_calculation?.base_price || 0,
            method: response.price_details?.pricing_method?.selected_type || 'kilometrique',
            pickupZone: response.price_details?.inputs?.pickup?.detected_zone_name,
            dropoffZone: response.price_details?.inputs?.dropoff?.detected_zone_name,
            surcharges: response.price_details?.surcharges || [],
            surchargesTotal: response.price_details?.surcharges_total || 0,
            distanceKm,
            durationMin,
            minDistanceKm,
            isUnavailable,
            unavailableReason: isUnavailable ? `Distance minimale requise : ${minDistanceKm} km` : null,
            success: true
          };
        } catch (err) {
          return { category: vehicle.category, success: false, error: err.message };
        }
      });

      const results = await Promise.all(pricePromises);
      const pricesMap = {};
      results.forEach(r => {
        if (r.success) {
          pricesMap[r.category] = r;
        }
      });
      setVehiclePrices(pricesMap);
      
      // Sauvegarder les infos de distance/durée (prendre le premier résultat)
      const firstSuccessful = results.find(r => r.success && r.distanceKm > 0);
      if (firstSuccessful) {
        setRouteInfo({
          distance_km: firstSuccessful.distanceKm,
          duration_min: firstSuccessful.durationMin
        });
        updateBookingData({
          pickup_zone: firstSuccessful.pickupZone,
          dropoff_zone: firstSuccessful.dropoffZone,
          pricing_method: firstSuccessful.method,
          route_distance_km: firstSuccessful.distanceKm,
          route_duration_min: firstSuccessful.durationMin
        });
      }
    } catch (err) {
      console.error('Price calculation error:', err);
      setPriceError('Erreur lors du calcul des prix');
    } finally {
      setLoadingPrices(false);
    }
  }, [bookingData.pickup, bookingData.dropoff, bookingData.pickup_datetime, bookingData.is_round_trip, vehicles, intermediateStops]);

  // Recalculer les prix quand les données changent (incluant les étapes intermédiaires)
  useEffect(() => {
    if (vehicles.length > 0 && bookingData.pickup?.latitude && bookingData.dropoff?.latitude) {
      const timeoutId = setTimeout(() => {
        calculateAllPrices();
      }, 500); // Debounce
      return () => clearTimeout(timeoutId);
    }
  }, [bookingData.pickup, bookingData.dropoff, bookingData.pickup_datetime, bookingData.is_round_trip, vehicles, intermediateStops, calculateAllPrices]);

  // Sync intermediate stops
  useEffect(() => {
    updateBookingData({ intermediate_stops: intermediateStops });
  }, [intermediateStops]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await axios.get(
            `${API_URL}/api/pricing/reverse-geocode?lat=${latitude}&lng=${longitude}`
          );
          if (response.data.success && response.data.address) {
            updateBookingData({
              pickup: {
                address: response.data.address,
                latitude: latitude,
                longitude: longitude,
                place_id: response.data.place_id || null
              }
            });
          }
        } catch (err) {
          console.error('Geocoding error:', err);
        } finally {
          setGeolocating(false);
        }
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
        setGeolocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const addIntermediateStop = () => {
    setIntermediateStops([...intermediateStops, { address: '', latitude: 0, longitude: 0, place_id: null }]);
  };

  const removeIntermediateStop = (index) => {
    setIntermediateStops(intermediateStops.filter((_, i) => i !== index));
  };

  const updateIntermediateStop = (index, value) => {
    const newStops = [...intermediateStops];
    newStops[index] = value;
    setIntermediateStops(newStops);
  };

  const handleVehicleSelect = (category) => {
    updateBookingData({ vehicle_category: category });
    
    // Sauvegarder le prix sélectionné
    const selectedPrice = vehiclePrices[category];
    if (selectedPrice) {
      updateBookingData({
        price_estimate: {
          total: selectedPrice.price,
          is_provisional: false
        }
      });
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (!bookingData.pickup?.address) {
      newErrors.pickup = "Adresse de départ requise";
    }
    if (!bookingData.dropoff?.address) {
      newErrors.dropoff = "Adresse d'arrivée requise";
    }
    if (!bookingData.pickup_datetime) {
      newErrors.pickup_datetime = "Date et heure requises";
    } else {
      const dt = new Date(bookingData.pickup_datetime);
      const hour = dt.getHours();
      if (hour < serviceHours.start_hour || hour >= serviceHours.end_hour) {
        // Skip validation if night service is allowed (24/7 service)
        if (!serviceHours.allow_night_service) {
          newErrors.pickup_datetime = `Service disponible de ${serviceHours.start_hour}h à ${serviceHours.end_hour}h`;
        }
      }
    }
    if (!bookingData.vehicle_category) {
      newErrors.vehicle = "Veuillez sélectionner un véhicule";
    }
    if (bookingData.is_round_trip && !bookingData.return_datetime) {
      newErrors.return_datetime = "Date de retour requise pour un aller-retour";
    }
    // Validation numéro de vol/train si aéroport/gare détecté
    if ((pickupType === 'airport' || dropoffType === 'airport') && !bookingData.flight_number) {
      // Warning mais pas bloquant
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      clearErrors();
      nextStep();
    }
  };

  const getMinDateTime = () => {
    return new Date().toISOString().slice(0, 16);
  };

  // Véhicule sélectionné
  const selectedVehicle = vehicles.find(v => v.category === bookingData.vehicle_category);
  const selectedPrice = vehiclePrices[bookingData.vehicle_category];

  // Déterminer si un aéroport ou gare est impliqué
  const hasAirport = pickupType === 'airport' || dropoffType === 'airport';
  const hasTrainStation = pickupType === 'train' || dropoffType === 'train';

  return (
    <div className="space-y-6" data-testid="booking-step-1">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
          Votre trajet
        </h2>
        <p className="text-[#E5E5E5]/60 text-sm">
          Indiquez les détails de votre course
        </p>
      </div>

      {/* Pickup Address */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-6 flex flex-col items-center pt-8">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <div className="flex-1 w-0.5 bg-[#C9A24D]/30 my-1" />
        </div>
        <div className="pl-8">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[#E5E5E5] flex items-center gap-2">
              Adresse de départ
              {pickupType === 'airport' && <Plane className="w-4 h-4 text-blue-400" />}
              {pickupType === 'train' && <Train className="w-4 h-4 text-orange-400" />}
            </Label>
            {geolocating ? (
              <span className="text-xs text-[#C9A24D] flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Localisation...
              </span>
            ) : (
              <button
                type="button"
                onClick={getCurrentLocation}
                className="text-xs text-[#C9A24D] hover:text-[#E6C77A] flex items-center gap-1"
              >
                <Navigation className="w-3 h-3" />
                Ma position
              </button>
            )}
          </div>
          <GooglePlacesAutocomplete
            value={bookingData.pickup}
            onChange={(value) => updateBookingData({ pickup: value })}
            placeholder="Ex: Aéroport CDG Terminal 2E"
            error={validationErrors.pickup}
            testId="pickup-address"
          />
          
          {/* Vol/Train sous l'adresse de départ si aéroport/gare détecté */}
          {pickupType === 'airport' && (
            <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg animate-in slide-in-from-top-2">
              <div className="flex items-center gap-2 mb-1">
                <Plane className="w-4 h-4 text-blue-400" />
                <Label className="text-[#E5E5E5] text-sm">Numéro de vol d'arrivée</Label>
              </div>
              <Input
                type="text"
                value={bookingData.flight_number || ''}
                onChange={(e) => updateBookingData({ flight_number: e.target.value.toUpperCase() })}
                placeholder="Ex: AF1234"
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] uppercase h-9"
                data-testid="pickup-flight-number"
              />
              <p className="text-xs text-[#E5E5E5]/40 mt-1">
                Suivi temps réel et adaptation en cas de retard
              </p>
            </div>
          )}
          {pickupType === 'train' && (
            <div className="mt-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg animate-in slide-in-from-top-2">
              <div className="flex items-center gap-2 mb-1">
                <Train className="w-4 h-4 text-orange-400" />
                <Label className="text-[#E5E5E5] text-sm">Numéro de train d'arrivée</Label>
              </div>
              <Input
                type="text"
                value={bookingData.train_number || ''}
                onChange={(e) => updateBookingData({ train_number: e.target.value.toUpperCase() })}
                placeholder="Ex: TGV6789"
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] uppercase h-9"
                data-testid="pickup-train-number"
              />
            </div>
          )}
        </div>
      </div>

      {/* Intermediate Stops */}
      {intermediateStops.map((stop, index) => (
        <div key={index} className="relative animate-in slide-in-from-top-2">
          <div className="absolute left-0 top-0 bottom-0 w-6 flex flex-col items-center pt-8">
            <div className="w-3 h-3 rounded-full bg-[#C9A24D]" />
            <div className="flex-1 w-0.5 bg-[#C9A24D]/30 my-1" />
          </div>
          <div className="pl-8">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <GooglePlacesAutocomplete
                  label={`Étape ${index + 1}`}
                  value={stop}
                  onChange={(value) => updateIntermediateStop(index, value)}
                  placeholder="Adresse de l'étape"
                  testId={`intermediate-stop-${index}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeIntermediateStop(index)}
                className="mt-6 text-red-400 hover:text-red-300 hover:bg-red-400/10"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}

      {/* Add Stop Button */}
      <div className="relative">
        <div className="absolute left-0 top-0 w-6 flex justify-center">
          <div className="w-0.5 h-full bg-[#C9A24D]/30" />
        </div>
        <div className="pl-8">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addIntermediateStop}
            className="w-full border-dashed border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10"
            data-testid="add-stop-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une étape
          </Button>
        </div>
      </div>

      {/* Dropoff Address */}
      <div className="relative">
        <div className="absolute left-0 top-0 w-6 flex flex-col items-center pt-8">
          <div className="w-3 h-3 rounded-full bg-red-500" />
        </div>
        <div className="pl-8">
          <Label className="text-[#E5E5E5] flex items-center gap-2 mb-1">
            Adresse d'arrivée
            {dropoffType === 'airport' && <Plane className="w-4 h-4 text-blue-400" />}
            {dropoffType === 'train' && <Train className="w-4 h-4 text-orange-400" />}
          </Label>
          <GooglePlacesAutocomplete
            value={bookingData.dropoff}
            onChange={(value) => updateBookingData({ dropoff: value })}
            placeholder="Ex: 10 Rue de Rivoli, Paris"
            error={validationErrors.dropoff}
            testId="dropoff-address"
          />
          
          {/* Vol/Train sous l'adresse d'arrivée si aéroport/gare détecté */}
          {dropoffType === 'airport' && (
            <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg animate-in slide-in-from-top-2">
              <div className="flex items-center gap-2 mb-1">
                <Plane className="w-4 h-4 text-blue-400" />
                <Label className="text-[#E5E5E5] text-sm">Numéro de vol de départ</Label>
              </div>
              <Input
                type="text"
                value={bookingData.departure_flight_number || ''}
                onChange={(e) => updateBookingData({ departure_flight_number: e.target.value.toUpperCase() })}
                placeholder="Ex: AF5678"
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] uppercase h-9"
                data-testid="dropoff-flight-number"
              />
              <p className="text-xs text-[#E5E5E5]/40 mt-1">
                Pour adapter l'heure de prise en charge
              </p>
            </div>
          )}
          {dropoffType === 'train' && (
            <div className="mt-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg animate-in slide-in-from-top-2">
              <div className="flex items-center gap-2 mb-1">
                <Train className="w-4 h-4 text-orange-400" />
                <Label className="text-[#E5E5E5] text-sm">Numéro de train de départ</Label>
              </div>
              <Input
                type="text"
                value={bookingData.departure_train_number || ''}
                onChange={(e) => updateBookingData({ departure_train_number: e.target.value.toUpperCase() })}
                placeholder="Ex: TGV1234"
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] uppercase h-9"
                data-testid="dropoff-train-number"
              />
            </div>
          )}
        </div>
      </div>

      {/* Route Info - Distance & Duration */}
      {(bookingData.pickup?.latitude && bookingData.dropoff?.latitude) && (
        <div className="bg-[#1F1F1F] rounded-lg border border-[#C9A24D]/20 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C9A24D]/10 rounded-full flex items-center justify-center">
                <Navigation className="w-5 h-5 text-[#C9A24D]" />
              </div>
              <div>
                <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider">Itinéraire</p>
                {loadingPrices ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#C9A24D]" />
                    <span className="text-[#E5E5E5]/60 text-sm">Calcul en cours...</span>
                  </div>
                ) : routeInfo.distance_km ? (
                  <p className="text-[#E5E5E5] font-medium">
                    {Math.round(routeInfo.distance_km)} km
                    {intermediateStops.filter(s => s.address).length > 0 && (
                      <span className="text-[#C9A24D] text-sm ml-2">
                        ({intermediateStops.filter(s => s.address).length} étape{intermediateStops.filter(s => s.address).length > 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                ) : (
                  <span className="text-[#E5E5E5]/60 text-sm">En attente du calcul...</span>
                )}
              </div>
            </div>
            {!loadingPrices && routeInfo.duration_min > 0 && (
              <div className="text-right">
                <p className="text-[#E5E5E5]/60 text-xs uppercase tracking-wider">Durée estimée</p>
                <p className="text-[#E5E5E5] font-medium">
                  {routeInfo.duration_min >= 60 
                    ? `${Math.floor(routeInfo.duration_min / 60)}h${routeInfo.duration_min % 60 > 0 ? ` ${Math.round(routeInfo.duration_min % 60)}min` : ''}`
                    : `${Math.round(routeInfo.duration_min)} min`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Date and Time */}
      <div className="space-y-2">
        <Label className="text-[#E5E5E5]">Date et heure de prise en charge</Label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A24D]" />
          <Input
            type="datetime-local"
            value={bookingData.pickup_datetime}
            onChange={(e) => updateBookingData({ pickup_datetime: e.target.value })}
            min={getMinDateTime()}
            className="pl-10 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50"
            data-testid="pickup-datetime"
          />
        </div>
        {validationErrors.pickup_datetime && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {validationErrors.pickup_datetime}
          </p>
        )}
      </div>

      {/* Round Trip */}
      <div className="flex items-start space-x-3 p-4 bg-[#1F1F1F] rounded-lg border border-[#C9A24D]/10">
        <Checkbox
          id="round-trip"
          checked={bookingData.is_round_trip}
          onCheckedChange={(checked) => updateBookingData({ 
            is_round_trip: checked, 
            return_datetime: checked ? bookingData.return_datetime : '' 
          })}
          className="mt-0.5 border-[#C9A24D] data-[state=checked]:bg-[#C9A24D] data-[state=checked]:text-[#0B0B0B]"
          data-testid="round-trip-checkbox"
        />
        <div className="flex-1">
          <label htmlFor="round-trip" className="text-[#E5E5E5] font-medium cursor-pointer">
            Aller-retour
          </label>
          <p className="text-[#E5E5E5]/40 text-xs mt-0.5">
            Bénéficiez d'une remise sur le trajet retour
          </p>
        </div>
      </div>

      {/* Return DateTime */}
      {bookingData.is_round_trip && (
        <div className="space-y-2 animate-in slide-in-from-top-2">
          <Label className="text-[#E5E5E5]">Date et heure de retour</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A24D]" />
            <Input
              type="datetime-local"
              value={bookingData.return_datetime}
              onChange={(e) => updateBookingData({ return_datetime: e.target.value })}
              min={bookingData.pickup_datetime || getMinDateTime()}
              className="pl-10 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50"
              data-testid="return-datetime"
            />
          </div>
          {validationErrors.return_datetime && (
            <p className="text-red-400 text-sm">{validationErrors.return_datetime}</p>
          )}
        </div>
      )}

      {/* Vehicle Selection with Prices */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-[#E5E5E5]">Choisissez votre véhicule</Label>
          {loadingPrices && (
            <span className="text-xs text-[#C9A24D] flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Calcul des tarifs...
            </span>
          )}
        </div>
        
        {validationErrors.vehicle && (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {validationErrors.vehicle}
          </p>
        )}

        {priceError && (
          <p className="text-yellow-400 text-sm flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {priceError}
          </p>
        )}

        <div className="space-y-3">
          {vehicles.map((vehicle) => {
            const isSelected = bookingData.vehicle_category === vehicle.category;
            const priceInfo = vehiclePrices[vehicle.category];
            const canAccommodate = (bookingData.passengers || 1) <= vehicle.max_passengers && 
                                   (bookingData.luggage || 0) <= vehicle.max_luggage;
            const isUnavailable = priceInfo?.isUnavailable;
            const canSelect = canAccommodate && !isUnavailable;
            
            return (
              <Card
                key={vehicle.category}
                onClick={() => canSelect && handleVehicleSelect(vehicle.category)}
                className={`cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-[#C9A24D]/10 border-[#C9A24D] ring-2 ring-[#C9A24D]/30'
                    : canSelect
                      ? 'bg-[#1F1F1F] border-[#C9A24D]/10 hover:border-[#C9A24D]/30'
                      : 'bg-[#1F1F1F]/50 border-[#E5E5E5]/10 opacity-50 cursor-not-allowed'
                }`}
                data-testid={`vehicle-${vehicle.category}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#C9A24D]/20' : 'bg-[#0B0B0B]'}`}>
                        <Car className={`w-6 h-6 ${isSelected ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/60'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#E5E5E5]">{vehicle.name}</h3>
                        {vehicle.description && (
                          <p className="text-xs text-[#E5E5E5]/50 mt-0.5 max-w-[200px]">{vehicle.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#E5E5E5]/60">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {vehicle.max_passengers} max
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {vehicle.max_luggage} bagages
                          </span>
                        </div>
                        {/* Message d'indisponibilité distance */}
                        {isUnavailable && priceInfo?.unavailableReason && (
                          <p className="text-xs text-orange-400 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {priceInfo.unavailableReason}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right flex items-center gap-3">
                      {/* Prix */}
                      {loadingPrices ? (
                        <div className="w-16 h-6 bg-[#E5E5E5]/10 rounded animate-pulse" />
                      ) : isUnavailable ? (
                        <span className="text-sm text-orange-400 font-medium">Indisponible</span>
                      ) : priceInfo ? (
                        <div>
                          <span className="text-xl font-bold text-[#C9A24D]">
                            {Math.round(priceInfo.price)}€
                          </span>
                          <span className="text-xs text-[#E5E5E5]/40 ml-1">TTC</span>
                          {priceInfo.method === 'zone_zone' && (
                            <div className="flex items-center gap-1 text-xs text-green-400 mt-0.5">
                              <Tag className="w-3 h-3" />
                              Forfait zone
                            </div>
                          )}
                          {/* Surcharges indicator */}
                          {priceInfo.surcharges && priceInfo.surcharges.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-indigo-400 mt-0.5" title={priceInfo.surcharges.map(s => `${s.name}: +${(s.amount || 0).toFixed(2)}€`).join(', ')}>
                              <Moon className="w-3 h-3" />
                              <span>
                                {priceInfo.surcharges[0]?.name || 'Supplément'} 
                                <span className="text-[#E5E5E5]/50 ml-1">(+{priceInfo.surchargesTotal?.toFixed(0) || 0}€)</span>
                              </span>
                            </div>
                          )}
                        </div>
                      ) : bookingData.pickup?.latitude && bookingData.dropoff?.latitude ? (
                        <span className="text-xs text-[#E5E5E5]/40">-</span>
                      ) : (
                        <span className="text-xs text-[#E5E5E5]/40">
                          Entrez les adresses
                        </span>
                      )}
                      
                      {/* Check mark */}
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-[#C9A24D] flex items-center justify-center">
                          <Check className="w-4 h-4 text-[#0B0B0B]" />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Zone Detection Info - Only show if both zones are defined (not "Hors zone") */}
      {selectedPrice && selectedPrice.pickupZone && selectedPrice.dropoffZone && 
       !selectedPrice.pickupZone.toLowerCase().includes('hors') && 
       !selectedPrice.dropoffZone.toLowerCase().includes('hors') && (
        <div className="p-3 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg" data-testid="zone-info">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-[#C9A24D]" />
            <span className="text-[#E5E5E5]/70">
              {selectedPrice.pickupZone} → {selectedPrice.dropoffZone}
            </span>
          </div>
        </div>
      )}

      {/* Surcharges Info Banner */}
      {selectedPrice && selectedPrice.surcharges && selectedPrice.surcharges.length > 0 && (
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg" data-testid="surcharges-info">
          <div className="flex items-center gap-2 text-sm mb-2">
            <Moon className="w-4 h-4 text-indigo-400" />
            <span className="text-indigo-300 font-medium">Suppléments appliqués</span>
          </div>
          <div className="space-y-1">
            {selectedPrice.surcharges.map((surcharge, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <span className="text-[#E5E5E5]/70">
                  {surcharge.name}
                  {surcharge.rate && <span className="text-[#E5E5E5]/40 ml-1">({surcharge.rate}%)</span>}
                </span>
                <span className="text-indigo-400 font-medium">+{(surcharge.amount || 0).toFixed(2)}€</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#E5E5E5]/40 mt-2">
            Ce supplément est automatiquement appliqué selon l'heure de prise en charge.
          </p>
        </div>
      )}

      {/* Price Summary */}
      {selectedPrice && bookingData.is_round_trip && (
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-[#E5E5E5]">Prix aller-retour avec remise</span>
            <span className="text-xl font-bold text-green-400">
              {Math.round(selectedPrice.price)}€ TTC
            </span>
          </div>
        </div>
      )}

      {/* Next Button */}
      <Button
        onClick={handleNext}
        disabled={!bookingData.pickup?.address || !bookingData.dropoff?.address || !bookingData.vehicle_category}
        className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold py-6 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="step1-next-button"
      >
        Continuer
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default Step1Trip;
